// import Thumbnail from "/thumbnail.png";
import { Link } from '@tanstack/react-router';
import { Star } from 'lucide-react';
import { Button } from '../ui/button';
import 'react-circular-progressbar/dist/styles.css';

const CourseCard = ({ course, dashboard }) => {
    // Helper to compute steps-based progress similar to LessonAppBar
    const computeProgress = courseObj => {
        if (!courseObj) return { totalSteps: 0, completedSteps: 0, percent: 0, width: 0 };

        // try to find user-specific progress payloads returned by list endpoints
        const userProgress =
            courseObj.user_progress ||
            courseObj.userProgress ||
            courseObj.progress_detail ||
            courseObj.progressDetail ||
            courseObj.progress_data ||
            null;

        // also check common nested locations (API adapters sometimes nest under attributes/data/meta)
        const nested = courseObj.attributes || (courseObj.data && courseObj.data.attributes) || courseObj.meta || null;
        const nestedUserProgress =
            nested && (nested.user_progress || nested.userProgress || nested.progress_detail || nested.progress || null);

        // Prefer explicit totals if provided anywhere
        let totalSteps = typeof courseObj.stepsTotal === 'number' ? courseObj.stepsTotal : null;
        if (totalSteps === null && userProgress && typeof userProgress.steps_total === 'number') {
            totalSteps = userProgress.steps_total;
        }
        if (totalSteps === null && userProgress && typeof userProgress.total_steps === 'number') {
            totalSteps = userProgress.total_steps;
        }
        if (totalSteps === null && nestedUserProgress && typeof nestedUserProgress.steps_total === 'number') {
            totalSteps = nestedUserProgress.steps_total;
        }
        if (totalSteps === null && nestedUserProgress && typeof nestedUserProgress.total_steps === 'number') {
            totalSteps = nestedUserProgress.total_steps;
        }

        let completedSteps = typeof courseObj.stepsCompleted === 'number' ? courseObj.stepsCompleted : null;
        if (completedSteps === null && userProgress && typeof userProgress.steps_completed === 'number') {
            completedSteps = userProgress.steps_completed;
        }
        if (completedSteps === null && nestedUserProgress && typeof nestedUserProgress.steps_completed === 'number') {
            completedSteps = nestedUserProgress.steps_completed;
        }

        // Top-level arrays (lessons/projects/quizes)
        if (totalSteps === null) {
            const lessonsCount = Array.isArray(courseObj.lessons) ? courseObj.lessons.length : 0;
            const projectsCount = Array.isArray(courseObj.projects) ? courseObj.projects.length : 0;
            const quizesCount = Array.isArray(courseObj.quizes) ? courseObj.quizes.length : 0;
            const topLevelTotal = lessonsCount + projectsCount + quizesCount;
            if (topLevelTotal > 0) totalSteps = topLevelTotal;
        }

        // Fallback: topics array containing lessons/projects/quizes
        if (totalSteps === null && Array.isArray(courseObj.topics)) {
            totalSteps = courseObj.topics.reduce((acc, t) => {
                const lessons = Array.isArray(t.lessons) ? t.lessons.length : 0;
                const projects = Array.isArray(t.projects) ? t.projects.length : 0;
                const quizes = Array.isArray(t.quizes) ? t.quizes.length : 0;
                return acc + lessons + projects + quizes;
            }, 0);
        }

        // Compute completed steps from known completion arrays or nested userProgress
        if (completedSteps === null) {
            let c = 0;
            if (userProgress) {
                if (typeof userProgress.completed_count === 'number') c += userProgress.completed_count;
                if (typeof userProgress.steps_completed === 'number') c += userProgress.steps_completed;
                if (Array.isArray(userProgress.completed_lessons)) c += userProgress.completed_lessons.length;
                if (Array.isArray(userProgress.completed_projects)) c += userProgress.completed_projects.length;
                if (Array.isArray(userProgress.completed_quizes)) c += userProgress.completed_quizes.length;
            }
            if (nestedUserProgress) {
                if (typeof nestedUserProgress.completed_count === 'number') c += nestedUserProgress.completed_count;
                if (typeof nestedUserProgress.steps_completed === 'number') c += nestedUserProgress.steps_completed;
                if (Array.isArray(nestedUserProgress.completed_lessons)) c += nestedUserProgress.completed_lessons.length;
                if (Array.isArray(nestedUserProgress.completed_projects)) c += nestedUserProgress.completed_projects.length;
                if (Array.isArray(nestedUserProgress.completed_quizes)) c += nestedUserProgress.completed_quizes.length;
            }

            if (Array.isArray(courseObj.completions)) c += courseObj.completions.length;
            if (Array.isArray(courseObj.projectCompletions)) c += courseObj.projectCompletions.length;
            if (Array.isArray(courseObj.quizCompletions)) c += courseObj.quizCompletions.length;

            // legacy field names
            if (Array.isArray(courseObj.completed_lessons)) c += courseObj.completed_lessons.length;
            if (Array.isArray(courseObj.completed_projects)) c += courseObj.completed_projects.length;
            if (Array.isArray(courseObj.completed_quizes)) c += courseObj.completed_quizes.length;

            completedSteps = c;
        }

        // If we have explicit step counts, derive percent
        if (typeof totalSteps === 'number' && totalSteps > 0 && typeof completedSteps === 'number') {
            const percent = Math.round((completedSteps / totalSteps) * 100);
            return {
                totalSteps,
                completedSteps,
                percent: Math.min(Math.max(percent, 0), 100),
                width: Math.min(Math.max(percent, 0), 100),
            };
        }

        // Fallback to numeric progress (0-1 or 0-100). Handle objects or strings.
        let raw = 0;
        const progressField = courseObj.progress ?? (nested && nested.progress) ?? userProgress ?? nestedUserProgress ?? null;
        if (progressField && typeof progressField === 'object') {
            // e.g. { percent: 0.04 } or { value: 4 }
            raw = Number(progressField.percent ?? progressField.value ?? progressField.amount ?? 0) || 0;
            if (raw > 1) {
                return {
                    totalSteps: null,
                    completedSteps: null,
                    percent: Math.round(raw),
                    width: Math.min(Math.max(Math.round(raw), 0), 100),
                };
            }
        } else {
            // handle string percentages like "4%" or numeric strings
            if (typeof progressField === 'string' && progressField.trim().endsWith('%')) {
                raw = Number(progressField.replace('%', '').trim()) || 0;
                return {
                    totalSteps: null,
                    completedSteps: null,
                    percent: Math.round(raw),
                    width: Math.min(Math.max(Math.round(raw), 0), 100),
                };
            }
            raw = Number(progressField ?? courseObj.progress ?? 0) || 0;
        }

        const percent = raw > 1 ? Math.round(raw) : Math.round(raw * 100);
        const width = Math.min(Math.max(raw > 1 ? raw : raw * 100, 0), 100);

        // if percent resolved to 0 but there are fields that suggest progress, log for investigation
        if (percent === 0) {
            const hint = {
                id: courseObj.id ?? courseObj.slug ?? null,
                progressField: progressField ?? null,
                userProgress: userProgress ?? null,
                nestedUserProgress: nestedUserProgress ?? null,
                hasTopic: Array.isArray(courseObj.topics),
                hasLessons: Array.isArray(courseObj.lessons),
            };
            console.debug('CourseCard: computed 0% for course, hint:', hint);
        }

        return { totalSteps: null, completedSteps: null, percent, width };
    };
    // Defensive slug used for navigation links in dashboard context
    const safeSlug = typeof course?.slug === 'string' && course.slug.trim() ? course.slug.trim() : null;
    if (dashboard) {
        return (
            <div className="flex flex-col md:flex-row gap-4 border border-neutral-300 rounded-2xl w-full max-w-3xl p-4">
                {/* Image Section */}
                <div className="relative w-full h-50 md:h-auto md:w-50 md:self-stretch shrink-0 bg-neutral-100 rounded-lg">
                    <img
                        src={
                            course.thumbnail ||
                            'https://lms-internal.zinduaschool.com/assets/thumbnails/default-course/default-thumbnail.png'
                        }
                        alt=""
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-32.5 max-h-32.5 object-contain"
                    />
                </div>

                {/* Details Section */}
                <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div className="shrink-0">
                        <h4 className="line-clamp-1 mb-2">{course.title}</h4>
                        <p className="line-clamp-2 mb-4">{course.summary}</p>
                    </div>

                    <div className="shrink-0 mb-4">
                        {(() => {
                            const { percent, width, totalSteps, completedSteps } = computeProgress(course);

                            return (
                                <div>
                                    <div className="w-full flex justify-between items-center mb-2">
                                        <div className="flex flex-row gap-1 md:gap-2">
                                            <strong className="text-sm text-success">Progress</strong>
                                            <strong className="text-sm text-success">{percent}%</strong>
                                        </div>
                                        <div>
                                            <strong className="hidden lg:block text-sm text-neutral-500">
                                                {typeof completedSteps === 'number' &&
                                                typeof totalSteps === 'number' &&
                                                totalSteps > 0
                                                    ? `${completedSteps}/${totalSteps} steps`
                                                    : `${percent}%`}
                                            </strong>
                                        </div>
                                    </div>
                                    <div className="w-full bg-neutral-200 rounded-full h-2">
                                        <div
                                            className="bg-success h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${width}%` }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>

                    <div className="shrink-0">
                        {course.is_completed ? (
                            <div className="flex flex-col lg:flex-row gap-2">
                                {safeSlug ? (
                                    <Link
                                        to="/course/$courseSlug"
                                        params={{ courseSlug: safeSlug }}
                                        preload="intent"
                                        preloadDelay={0}
                                        className="flex-1"
                                    >
                                        <Button
                                            variant="destructive"
                                            className="w-full flex justify-center items-center gap-2 py-2 "
                                        >
                                            View Course
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button variant="destructive" className="w-full" disabled>
                                        View Course
                                    </Button>
                                )}

                                {safeSlug ? (
                                    <Link
                                        to="/course/$courseSlug"
                                        params={{ courseSlug: safeSlug }}
                                        preload="intent"
                                        preloadDelay={0}
                                        className="flex-1"
                                    >
                                        <Button variant="outline" className="w-full flex justify-center items-center gap-2 py-2">
                                            View Certificate
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button variant="outline" className="w-full" disabled>
                                        View Certificate
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col min-[1184px]:flex-row gap-2">
                                {safeSlug ? (
                                    <Link
                                        to="/lessons/$courseSlug"
                                        params={{ courseSlug: safeSlug }}
                                        preload="intent"
                                        preloadDelay={0}
                                        className="flex-1"
                                    >
                                        <Button
                                            variant="destructive"
                                            className="w-full flex justify-center items-center gap-2 py-2"
                                        >
                                            Continue Learning
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button variant="destructive" className="w-full" disabled>
                                        Continue Learning
                                    </Button>
                                )}

                                {safeSlug ? (
                                    <Link
                                        to="/course/$courseSlug"
                                        params={{ courseSlug: safeSlug }}
                                        preload="intent"
                                        preloadDelay={0}
                                        className="flex-1"
                                    >
                                        <Button
                                            variant="outline"
                                            className="w-full flex justify-center items-center gap-2 py-2 text-sm"
                                        >
                                            View Course
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button variant="outline" className="w-full" disabled>
                                        View Course
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Non-dashboard view
    return (
        <div className="flex flex-col border border-neutral-300 rounded-2xl overflow-hidden cursor-pointer p-4">
            <div className="relative block mb-4 h-60 rounded-lg">
                <img
                    src={
                        course.thumbnail ||
                        'https://lms-internal.zinduaschool.com/assets/thumbnails/default-course/default-thumbnail.png'
                    }
                    alt=""
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 object-contain"
                />
            </div>

            <div className="flex flex-col grow">
                <h4 className="mb-1 line-clamp-1">{course.title}</h4>
                <p className="line-clamp-2 h-12 my-1">{course.summary}</p>
                {!course.is_enrolled && (
                    <div className="my-4 flex justify-between">
                        <div className="my-0.5">
                            <h5 className="">
                                {typeof course.price === 'number'
                                    ? course.price === 0
                                        ? 'FREE'
                                        : `KES ${course.price.toLocaleString()}`
                                    : 'FREE'}
                            </h5>
                        </div>
                        {course.rating !== null && course.rating !== undefined && (
                            <div className="flex items-center">
                                <div className="flex items-center">
                                    <Star className="w-4 h-4 text-gold" />
                                </div>
                                <span className="text-sm text-neutral-700 ml-2">
                                    {course.rating}({course.reviews} reviews)
                                </span>
                            </div>
                        )}
                    </div>
                )}
                {course.is_enrolled && (
                    <div className="my-4">
                        {(() => {
                            const { percent, width } = computeProgress(course);

                            return (
                                <>
                                    <p className="font-bold text-sm text-success">{percent}% COMPLETE</p>
                                    <div className="h-2 bg-neutral-200 rounded-full mt-1">
                                        <div
                                            className="h-full rounded-full bg-success transition-all duration-300"
                                            style={{ width: `${width}%` }}
                                        ></div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                )}

                <Button variant="outline" className="w-full md:w-auto py-1 my-1">
                    {course.is_enrolled ? (Number(course.progress) === 100 ? 'View Certificate' : 'Continue') : 'Learn More'}
                </Button>
            </div>
        </div>
    );
};

export default CourseCard;
